import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { useFeature } from '../../../services/features/reactBindings/hooks';
import { useChatSlice } from '../../../store/slices/chat/selectors';
import { SendType } from '../../../store/slices/chat/types';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { HeaderModal } from '../../../store/slices/header/types';
import { useIsLargerThanWide } from '../../../styles/hooks/media';
import { parseSystemMessage } from '../../../utils/chat';
import { Ribbon } from '../../ribbon/Ribbon';
import { MessageContentConnected } from '../chat/messages/messageContent/MessageContentConnected';

import styles from './ChatRibbon.module.css';

const HIDE_DELAY = 3_000;

export const ChatRibbonConnected: FC = (): ReactNode => {
  const newMobileLayout = useFeature('newMobileLayout');
  const [isVisible, setIsVisible] = useState(true);
  const openedModal = useHeaderSlice.getCoreOpenedModal();

  const hasWinners = useGameStateSlice.winners().length;
  const isMenuRouteChat = useHeaderSlice.getNavRoute()[1] === 'CHAT';

  const isGameChatOpened = openedModal == HeaderModal.Chat;

  const message = useChatSlice.getLastChatMessage();
  const isUserSupportMessage = message?.tagMessage === 'SUPPORT' && message?.role === 'GAME_USER';
  const messageUser = message?.userScreenName;
  const messageContent = message?.content;
  const requireTranslation = message?.requireTranslation;
  const isSystemMessage = message?.role === 'SUPPORT' && message?.sendType === SendType.Table;

  const isWide = useIsLargerThanWide();
  const isChatDisabled = useConfigSlice.isChatDisabled();

  // Hide component if last message hasn't been updated for 3000ms
  useEffect(() => {
    let timeoutId: number;

    if (messageContent) {
      setIsVisible(true);
      timeoutId = window.setTimeout(() => {
        setIsVisible(false);
      }, HIDE_DELAY);
    }

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [messageContent]);

  if (isChatDisabled || hasWinners || !isVisible || !messageContent || (isGameChatOpened && isMenuRouteChat)) {
    return null;
  }

  const generateMessageContent = (): ReactNode => {
    if (isSystemMessage) {
      return parseSystemMessage(messageContent); // Remove HTML tags from system message
    }

    return <MessageContentConnected content={messageContent} requireTranslation={requireTranslation} />;
  };

  if (isWide || isUserSupportMessage) return null;

  return newMobileLayout ? (
    <Ribbon label={messageUser ? `${messageUser}:` : ''} onewayFade>
      {generateMessageContent()}
    </Ribbon>
  ) : (
    <div data-testid="chat-ribbon" className={styles.chatRibbon} aria-label={messageUser ? `${messageUser}:` : ''}>
      <div data-testid="chat-ribbon-message" className={styles.chatRibbon__sliderCnt}>
        {generateMessageContent()}
      </div>
    </div>
  );
};
